// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentsList.test.tsx
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import {
  isMinorFinding,
  type PinGroup,
  placePoints,
  severityRank,
} from "./pinGroups";
import { type CommentWithOptionalTags } from "./reviewCommentTags";
import { useReviewCommentMoveNotice } from "./useReviewCommentMoveNotice";

import {
  REVIEW_COMMENT_GROUPS,
  type ReviewCommentGroup,
  reviewCommentGroup,
} from "../../utils/visibleReviewComments";

/**
 * The group's still-open findings.
 *
 * why: the group snapshot keeps a finding after she likes or rejects it, and
 * the section list files that same finding under Approved or Rejected.
 * Drawing the snapshot whole leaves a copy in Unresolved. All-pins never
 * passes `pinGroups`, so this filter does not touch that view.
 */
export function openPinGroup(
  group: PinGroup,
  listed?: ReadonlySet<string>,
): PinGroup | null {
  const members = group.members.filter(
    (member) =>
      reviewCommentGroup(member) === "unresolved" &&
      (listed == null || listed.has(member._id)),
  );
  if (members.length === 0) return null;
  // Stryker disable next-line ConditionalExpression: equivalent -- with no member filtered out the fall-through rebuilds the same group (same members, hub and points, and `urgent` already equals `members.some(severityRank === 2)`); the `true` replacement, returning the unfiltered snapshot, is killed by "drops a liked or rejected finding out of its unresolved group"
  if (members.length === group.members.length) return group;
  // why: a block's summary speaks for every finding in it, so a group the
  // list cuts down (changed comments only) lists its own findings' issues.
  // why-not for one member: like a place of one, its card already says it.
  const cut =
    listed != null && group.members.some((member) => !listed.has(member._id));
  return {
    ...group,
    members,
    urgent: members.some((member) => severityRank(member) === 2),
    ...(cut ? { points: placePoints(members) } : {}),
  };
}

type PinGroupsBulkInput = {
  groups: readonly PinGroup[];
  openGroupKey: string | null;
};

/**
 * Comments bulk Select all may reach: the open pin group's rendered members,
 * intersected with what the list view actually shows.
 */
export function bulkVisibleComments(
  comments: readonly CommentWithOptionalTags[],
  pinGroups: PinGroupsBulkInput | null | undefined,
): readonly CommentWithOptionalTags[] {
  if (pinGroups == null) return comments;
  const openKey = pinGroups.openGroupKey;
  // Stryker disable next-line ConditionalExpression: `-> false` is equivalent -- a null or undefined open key falls through to `groups.find`, whose keys are strings, so it matches no group and `rawGroup == null` still returns []; `-> true` returns [] for every input and is killed by "reaches the whole list without pin groups and nothing when no known group is open"
  if (openKey == null) return [];
  const rawGroup = pinGroups.groups.find((group) => group.key === openKey);
  if (rawGroup == null) return [];
  const listed = new Set(comments.map((comment) => comment._id));
  const open = openPinGroup(rawGroup, listed);
  if (open == null) return [];
  return open.members;
}

const SECTION_LABEL_KEYS = {
  unresolved: "askV2.review.sectionUnresolved",
  confirmed: "askV2.review.sectionConfirmed",
  rejected: "askV2.review.sectionRejected",
} as const;

type ReviewCommentSectionsInput = {
  comments: readonly CommentWithOptionalTags[];
  /** Comments before the filter, for the count each heading reports hidden. */
  allComments?: readonly CommentWithOptionalTags[];
  selectedCommentId: string | null;
  /** "All pins" view: Unresolved splits into the findings to fix and a minor band. */
  severityBands: boolean;
  /** The list draws Unresolved in pin groups, which turns the bands off. */
  grouped: boolean;
  /** To decide / All tabs are on (flag ask-v2-review-status-tabs). */
  statusTabs?: boolean;
  /** Puts a comment back to To decide; given, the moved toast offers Undo. */
  onUndoMove?: (commentId: string) => void;
  /** Ids a bulk verdict is writing; their moves raise no toast of their own. */
  quietMoves?: { readonly current: Set<string> };
  /** Open pin group for bulk selection; same `openPinGroup` as the list render. */
  pinGroups?: PinGroupsBulkInput | null;
};

/**
 * Which section each finding sits in, which sections and the minor band are
 * open, and the effects that keep the selected card in sight.
 */
export function useReviewCommentSections({
  comments,
  allComments,
  selectedCommentId,
  severityBands,
  grouped: pinGrouped,
  statusTabs = false,
  onUndoMove,
  quietMoves,
  pinGroups = null,
}: ReviewCommentSectionsInput) {
  // Findings are grouped by what has been decided on them now: Unresolved
  // holds every finding nobody has approved or rejected, new in this review or
  // raised in a past one. A judgment made here files the card under its new
  // section at once, so the counts and the buckets always agree with the
  // server's copy the judgment patched in. All three always render, empty ones
  // included, so "Approved 0" says there is nothing approved yet rather than
  // leaving her to wonder where it went.
  // why: the To decide tab hands in only unresolved comments, so a judged card
  // would leave the array before the notice could see it move. Flag off keeps
  // the shown list: allComments also holds pin-group companions it never announced.
  useReviewCommentMoveNotice(
    statusTabs ? (allComments ?? comments) : comments,
    onUndoMove,
    quietMoves,
  );
  const grouped = useMemo(() => {
    const byGroup: Record<ReviewCommentGroup, CommentWithOptionalTags[]> = {
      unresolved: [],
      confirmed: [],
      rejected: [],
    };
    for (const comment of comments) {
      byGroup[reviewCommentGroup(comment)].push(comment);
    }
    return byGroup;
  }, [comments]);
  const sections = useMemo(
    () =>
      REVIEW_COMMENT_GROUPS.map((key) => ({
        key,
        labelKey: SECTION_LABEL_KEYS[key],
        items: grouped[key],
      })),
    [grouped],
  );
  // How many of each section's findings the filter hides, for its heading.
  const sectionSizes = useMemo(() => {
    const counts: Record<ReviewCommentGroup, number> = {
      unresolved: 0,
      confirmed: 0,
      rejected: 0,
    };
    for (const comment of allComments ?? comments) {
      if (comment.isDeleted) continue;
      counts[reviewCommentGroup(comment)] += 1;
    }
    return counts;
  }, [allComments, comments]);
  const unresolvedCount = grouped.unresolved.length;
  const showSections = comments.length > 0;
  // Only Unresolved starts open: it is the list she works through. Approved
  // and Rejected start collapsed (Surbhi, 2026-10-07), their counts still on
  // the heading, so a decided finding is one click away rather than gone.
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    () => new Set(["confirmed", "rejected"]),
  );
  // why: `sections` takes a new identity on every poll, filter change or added
  // comment; opening on that would reopen a section she closed while its
  // finding is still selected. Open only when the selection moves onto a
  // finding.
  const [revealMemory] = useState(() => {
    let revealed: string | null = null;
    return {
      get: () => revealed,
      set: (next: string | null) => {
        revealed = next;
      },
    };
  });
  const isSectionOpen = (key: string) => !collapsedSections.has(key);
  const [minorBandOpen, setMinorBandOpen] = useState(true);
  const bandsOn = severityBands && !pinGrouped;
  // why: a minor finding picked on the artwork must not land in the folded band, out of sight.
  const selectedInFoldedBand =
    bandsOn &&
    !minorBandOpen &&
    grouped.unresolved.some(
      (c) => c._id === selectedCommentId && isMinorFinding(c),
    );
  useEffect(() => {
    // Stryker disable next-line ConditionalExpression: `-> true` is equivalent -- the band starts open and the flag only turns true while it is closed, which this same effect reopens, so the extra open changes nothing a reader sees; `-> false` never reopens it and is killed by "opens the folded band when its finding is selected on the artwork"
    if (selectedInFoldedBand) setMinorBandOpen(true);
  }, [selectedInFoldedBand]);
  // why: a card she approves or rejects leaves its section for one that may
  // be collapsed; open that one so the card she just judged -- and its
  // Restore -- stays in sight (#5927). Only a move opens a section: a finding
  // a running review adds is not one, nor is a remount.
  const lastGroups = useRef<Map<string, ReviewCommentGroup> | null>(null);
  useEffect(() => {
    const before = lastGroups.current;
    const now = new Map<string, ReviewCommentGroup>();
    const movedInto: ReviewCommentGroup[] = [];
    for (const group of REVIEW_COMMENT_GROUPS) {
      for (const comment of grouped[group]) {
        now.set(comment._id, group);
        const was = before?.get(comment._id);
        if (was != null && was !== group && !movedInto.includes(group)) {
          movedInto.push(group);
        }
      }
    }
    lastGroups.current = now;
    setCollapsedSections((prev) => {
      // Stryker disable next-line ConditionalExpression: equivalent -- without the bail-out the updater returns a copy of prev with the same keys, so only render identity changes
      if (!movedInto.some((key) => prev.has(key))) return prev;
      const next = new Set(prev);
      movedInto.forEach((key) => next.delete(key));
      return next;
    });
  }, [grouped]);
  // why: selecting a finding from the artwork (its pin) must never land on a
  // card hidden inside a closed section -- open the section that holds it.
  useEffect(() => {
    if (!selectedCommentId) {
      revealMemory.set(null);
      return;
    }
    const holder = sections.find((section) =>
      section.items.some((comment) => comment._id === selectedCommentId),
    );
    if (!holder) return;
    if (revealMemory.get() === selectedCommentId) return;
    revealMemory.set(selectedCommentId);
    setCollapsedSections((prev) => {
      // Stryker disable next-line ConditionalExpression: `-> false` is equivalent -- a section already open does not hold the key, so the copy deletes nothing and has the same members, only a new identity; `-> true` never opens the holding section and is killed by "opens the closed section again when she selects its finding a second time"
      if (!prev.has(holder.key)) return prev;
      const next = new Set(prev);
      next.delete(holder.key);
      return next;
    });
  }, [selectedCommentId, sections, revealMemory]);
  // why: a finding selected out of a closed section renders only once the
  // effect above opens it, so scroll when its card can be found -- not only
  // when the selection moves, or it is left off-screen.
  const selectedShown =
    // Stryker disable next-line ConditionalExpression,LogicalOperator: the `-> true` replacements and the `&&`-to-`||` ones are equivalent -- this flag is read only by the scroll below, and wherever they differ the selected card is not in the DOM (selection is null, it sits in a closed section, or in the folded minor band), so the query finds nothing and scrolls nothing anyway; any `-> false` replacement never scrolls and is killed by "scrolls to a lone minor finding selected on the artwork with the bands off"
    selectedCommentId != null &&
    !selectedInFoldedBand &&
    (!showSections ||
      sections.some(
        (section) =>
          isSectionOpen(section.key) &&
          // Stryker disable next-line ConditionalExpression: `-> true` is equivalent -- a card is only in the DOM inside an open section, so when the selected id is not in this section's items the query below finds no card and scrolls nothing; `-> false` and the `!==` replacement match nothing and are killed by "scrolls to a lone minor finding selected on the artwork with the bands off"
          section.items.some((comment) => comment._id === selectedCommentId),
      ));
  useLayoutEffect(() => {
    // Stryker disable next-line ConditionalExpression,LogicalOperator: `-> false` and the `||`-to-`&&` one are equivalent -- the extra cases let through have no selected card in the DOM (no selection, a closed section, or the folded minor band), so the query below finds nothing and scrolls nothing; `-> true` never scrolls and is killed by "scrolls to a lone minor finding selected on the artwork with the bands off"
    if (!selectedCommentId || !selectedShown) return;
    document
      .querySelector(
        `[data-testid="ask-v2-review-comment-card-${selectedCommentId}"]`,
      )
      ?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [selectedCommentId, selectedShown]);
  const toggleSection = (key: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const toggleMinorBand = () => setMinorBandOpen((open) => !open);

  const bulkPool = useMemo(
    () => bulkVisibleComments(comments, pinGroups),
    [comments, pinGroups],
  );

  return {
    sections,
    sectionSizes,
    unresolvedCount,
    showSections,
    isSectionOpen,
    toggleSection,
    bandsOn,
    minorBandOpen,
    toggleMinorBand,
    bulkPool,
  };
}
